/**
 * MONSTER TOWN — ERRAND CARDS + THE GUIDE ARROW. ERRAND CREW (src/ui/errands*).
 *
 * The whole of the expansion's brief §3 that a kid can actually see:
 *
 *   "Presented as pictures only: a card showing the giver's face and a 3-icon
 *    step strip, current step pulsing. A guide arrow points to the next place.
 *    Tapping the card re-shows what to do. Never fails, never times out. The
 *    kid can hold several at once and do them in any order."
 *
 * ===========================================================================
 *  WHAT IS ON A CARD, AND WHY EACH THING IS A PICTURE
 * ===========================================================================
 *   the face      WHO asked. A real cel-shaded monster portrait, the same
 *                 renderer that draws the kid's own monster, stable for ever
 *                 per giver id (see src/errands/giver.ts). A `talk` chip wears
 *                 the SAME portrait — one person is one face on this card.
 *   the strip     WHAT the errand is: ONE CHIP PER STEP, up to `MAX_CHIPS`, and
 *                 the card is as wide as its own errand. Finished ones are
 *                 stamped with a tick, the current one is bigger, orange and
 *                 pulsing. (The contract's words are "a 3-icon step strip";
 *                 three chips dropped `make` off the reference errand — the
 *                 kitchen, the whole point of this expansion — so the brief
 *                 wins. Past `MAX_CHIPS` the old window returns with the LAST
 *                 step pinned, so the destination is always on the card. The
 *                 long version is on `stripWindow` below.)
 *   the pips      HOW MANY. One pip per thing, ON the chip that wants them,
 *                 hollow until done. Countable, one to one.
 * There is no text node anywhere in this file. `errand-check` asserts the
 * layer's `textContent` is empty, the way `tutorial-check` does for the paw.
 *
 * ===========================================================================
 *  LAW #2, STRUCTURALLY
 * ===========================================================================
 * There is no dismiss, no decline, no close, no timer and no countdown in this
 * file. The only control other than "show me again" is TUCK, which calls
 * `errands.setCardHidden()` — that puts the CARD away, never the errand, and
 * the tucked deck is still a row of 88px faces one tap from coming back. There
 * is no state a kid can reach from which an errand is gone.
 *
 * ===========================================================================
 *  COST
 * ===========================================================================
 * The deck is rebuilt on EVENTS, never per frame. The only per-frame work is
 * the guide arrow's projection, which runs once every `PROJECT_EVERY` fixed
 * ticks (eight times a second at 30 Hz), does one cached layout read, and is
 * skipped entirely when there is nothing to point at — which is most of the
 * game's life. Same budget discipline as `signposts.ts`.
 */

import './errands.css'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { save } from '../core/save'
import { errands, getErrand, stepNeed, type ErrandCard, type ErrandStep } from '../core/errands'
import type { MonsterConfig, Vec3 } from '../core/types'
import { worldToCanvasPx } from '../input/pick'
import { app, type GameContext } from '../app/context'
import { giverLook } from '../errands/giver'
import { offerNow, offerReport, setAutoOffers } from '../errands/offers'
import { answersFor, requestReport, townCount } from '../errands/requests'
import { knownPlaceIds, resolveTarget, type GuideTarget } from '../errands/targets'
import { button, clear, el, onTap } from './dom'
import { errandIcon } from './errandsArt'
import { monsterPortrait } from './monsterArt'

/** Re-project the arrow this often. 4 = about eight times a second at 30 Hz. */
const PROJECT_EVERY = 4
/** How close to the edge of the glass the arrow lies down and points instead. */
const EDGE_PAD_PX = 64
/** How long the "show me again" nudge runs on the current step chip. */
const SHOUT_MS = 1100
/**
 * The arrival card lets itself out. Matches the CSS animation total.
 *
 * 4.2 s, the same as the away-report toast — this is a picture a three-year-old
 * is meant to LOOK at ("a monster gave you something to do"), not a status
 * flash. It was 2.7 s and that was brisk.
 */
const ARRIVAL_MS = 4200

export interface ErrandsUiApi {
  /** Rebuild the deck now. Cheap; used by tests and by scene changes. */
  repaint(): void
  /** Which errand the guide arrow is following. */
  focus(): string | null
  setFocus(id: string | null): void
}

export function installErrandsUi(ctx: GameContext, hud: HTMLElement): ErrandsUiApi {
  const deck = el('div', 'mt-errands')
  deck.dataset['tucked'] = '0'
  hud.append(deck)

  const guide = el('div', 'mt-guide')
  guide.style.display = 'none'
  guide.append(errandIcon('guide', 66))
  hud.append(guide)

  let focused: string | null = null
  let tucked = false
  let arrival: HTMLElement | null = null
  let tick = 0
  let canvasBox: { w: number; h: number } | null = null
  const remeasure = (): void => {
    canvasBox = null
    chrome = null
  }
  window.addEventListener('resize', remeasure)
  window.addEventListener('orientationchange', remeasure)
  bus.on('ui:panel', remeasure)
  // A door changes which crews' chrome is on the screen (the venue's own
  // controls come and go with the room), and the HUD is hidden for the length
  // of the transition — see the note on `measureChrome`.
  bus.on('scene:entered', remeasure)
  bus.on('app:modeChanged', remeasure)

  // -- the deck -------------------------------------------------------------

  /**
   * Cards worth showing: running, and not already finished. Hidden ones are
   * still here — they become the tucked face chips, because "put away" must
   * never mean "gone" (LAW #2).
   */
  const liveCards = (): ErrandCard[] => errands.cards().filter((c) => !c.done)

  const face = (giver: string, sizePx: number): HTMLElement => {
    const wrap = el('span', 'mt-errand-face')
    const look: MonsterConfig = giverLook(giver)
    wrap.append(monsterPortrait(look, sizePx))
    return wrap
  }

  const shout = (node: HTMLElement): void => {
    node.dataset['shout'] = '1'
    window.setTimeout(() => delete node.dataset['shout'], SHOUT_MS)
  }

  /**
   * THE STEP STRIP — AND IT DRAWS THE WHOLE ERRAND.
   *
   * ==========================================================================
   *  THE MIDDLE OF THE ERRAND IS NOT CONTEXT EITHER (fixed vr2 — MAJOR)
   * ==========================================================================
   * This was a three-chip window: the current step, the last step, and one
   * neighbour. On the reference errand (talk · gather · MAKE · deliver) that
   * dropped `make` on the first two steps — measured with
   * `tools/critic-vr2-card.mjs`, which found exactly three 44 px chips at
   * x = 34 / 84 / 134 and read `shots/critic-vr2-probe/zoo-card-padded.png` as
   * *talk / apple-with-three-dots / animal-face*. **The bowl was not drawn.**
   *
   * That is the one beat this whole expansion is about — *"whip something up in
   * the kitchen and bring it"* — silently hidden on the only instruction a
   * pre-reader has. The contract's words are "a 3-icon step strip"; the brief's
   * words are the errand. The brief outranks the contract, so the strip now
   * shows EVERY step and the CARD grows to hold them (see `[data-steps]` in
   * errands.css). Nothing shrinks: the chips are the same 44/56 px pictures
   * that were measured legible in round 1.
   *
   * ==========================================================================
   *  THE WINDOW IS STILL HERE, AND IT IS THE BOUND
   * ==========================================================================
   * A card cannot grow forever on a 1024 px iPad, so past `MAX_CHIPS` the old
   * window takes over — and it keeps the rule round 1 was about: the LAST step
   * is pinned, so the destination is on the card from the first second and
   * `make` (a bowl) and `deliver` (the animals) are never the same picture.
   * Nothing in `src/content/errands.ts` has more than four steps today, so
   * every shipped errand draws in full; the window exists so a five-step drop
   * degrades instead of overflowing.
   */
  const MAX_CHIPS = 4
  const stripWindow = (total: number, current: number): number[] => {
    if (total <= MAX_CHIPS) return [...Array(total).keys()]
    const last = total - 1
    const want = new Set<number>([Math.max(0, Math.min(current, last)), last])
    for (const i of [current - 1, current + 1, current - 2, current + 2, 0]) {
      if (want.size >= MAX_CHIPS) break
      if (i >= 0 && i <= last) want.add(i)
    }
    return [...want].sort((a, b) => a - b).slice(0, MAX_CHIPS)
  }

  /**
   * HOW MANY, ON THE PICTURE OF THE THING (fixed vr1).
   *
   * Critic, major, on the neighbour request: *"a single flower icon for 'three
   * flowers' with no count, no dots, no multiplier. The brief names this exact
   * errand; the card cannot express the number."* True, and it was true of
   * every step that was not the one the kid was on — the counting dots lived in
   * the card's top-right corner, counted only the CURRENT step, and were
   * spatially detached from the chip they belonged to.
   *
   * The count now rides on the chip, one pip per thing, which is what "countable,
   * one to one" actually means. Done chips get no pips: they wear the tick, the
   * tick means all of them, and two badges on one 44 px chip is a smudge (that
   * lesson is written up on `mt-step-tick` below).
   *
   * SHAPE, NOT ONLY HUE — see `.mt-pip` in errands.css and the same fix on
   * `.mt-dot` in ui.css. An undone pip is a hollow ring, a done one is filled.
   */
  const pips = (need: number, done: number): HTMLElement => {
    const row = el('span', 'mt-step-need')
    for (let i = 0; i < Math.min(need, 6); i++) {
      const p = el('span', 'mt-pip')
      p.dataset['done'] = String(i < done)
      row.append(p)
    }
    return row
  }

  /**
   * WHAT GOES INSIDE ONE CHIP.
   *
   * A `talk` step draws THE GIVER, not a generic monster. Critic, major:
   * *"the ACTIVE step chip, on the same card, contains a completely DIFFERENT
   * monster head. Two faces for one person on one card."* It did: the shared
   * `talk` picture used to have its own little teal monster in it, so the card
   * showed Fen at the top and a stranger in the chip underneath. The `talk`
   * drawing is now a speech bubble and nothing else (see errandsArt.ts), and the
   * chip wears the real portrait of the monster the step names, with the bubble
   * as a corner badge. One person, one face, twice.
   */
  const chipArt = (step: ErrandStep | undefined, icon: string, size: number): HTMLElement[] => {
    if (step?.kind !== 'talk') return [errandIcon(icon, size)]
    const badge = el('span', 'mt-step-say')
    badge.append(errandIcon('talk', 18))
    return [monsterPortrait(giverLook(step.npc), size), badge]
  }

  const buildCard = (card: ErrandCard): HTMLElement => {
    const node = el('button', 'mt-errand')
    node.setAttribute('type', 'button')
    // Dev-only, never rendered — the same rule `devName` lives under (LAW #4).
    node.setAttribute('aria-label', card.def.devName)
    node.dataset['id'] = card.id
    node.dataset['focus'] = '1'

    // Row 1: who asked. The counts moved onto the chips they count — see `pips`.
    const top = el('span', 'mt-errand-top')
    top.append(face(card.giver, 58))
    node.append(top)

    // Row 2: the strip, full width, big enough to name out loud.
    const strip = el('span', 'mt-errand-strip')
    const lastIndex = card.strip.length - 1
    const shown = stripWindow(card.strip.length, card.stepIndex)
    // HOW WIDE THE CARD HAS TO BE, declared as data rather than measured: the
    // chips are a fixed size, so the number of them is the whole answer. The
    // matching widths are in errands.css, beside the chip sizes they add up.
    node.dataset['steps'] = String(shown.length)
    for (const i of shown) {
      const s = card.strip[i]
      if (s === undefined) continue
      const step = card.def.steps[i]
      const chip = el('span', 'mt-step')
      chip.dataset['state'] = s.done ? 'done' : s.current ? 'current' : 'todo'
      /*
       * WHERE IT ENDS IS NOT CONTEXT (fixed vr1, round 1 — the SAME major again).
       *
       * Pinning the last step into the strip fixed the full card and did nothing
       * at all for the COMPACT row, which hid every chip that was not the current
       * one — so on the hand-me-down iPad the reference errand's card was a face
       * and a speech bubble, and "my garden needs three flowers" was a face and a
       * speech bubble with no flower and no three. Both of the critic's captures,
       * reproduced exactly, one layout down. Read off
       * `shots/fix-errands-vr1/01b`+`02b`, not off the code.
       *
       * The destination is now marked, and compact keeps it (see errands.css).
       * The row is CURRENT + WHERE IT ENDS, which is the smallest picture that
       * can still answer "what am I doing" and "where do I take it".
       */
      if (i === lastIndex) chip.dataset['last'] = '1'
      chip.append(...chipArt(step, s.icon, s.current ? 40 : 30))
      // A TICK BADGE, NOT A TICK ON TOP OF THE PICTURE. Stamping the tick across
      // the chip collided with the icon underneath and both became a smudge —
      // read off the close-up capture, not off the code. The picture of what was
      // done stays legible and the tick sits in the corner beside it.
      if (s.done) chip.append(el('span', 'mt-step-tick'))
      else {
        const need = step === undefined ? 1 : stepNeed(step)
        if (need > 1) chip.append(pips(need, s.current ? Math.min(card.have, need) : 0))
      }
      strip.append(chip)
    }
    node.append(strip)

    onTap(node, () => {
      shout(node)
      // "Show me again" IS a request to re-aim the arrow, so it goes out on the
      // wire that means exactly that — which also makes the re-show observable
      // to a tool without reading a transient CSS attribute.
      setFocus(card.id)
      // Supports the picture, never replaces it — nothing here waits on voice.
      bus.emit('audio:cue', { cue: 'sparkle_soft' })
      const step = card.step
      if (step?.voice !== undefined) bus.emit('audio:voice', { key: step.voice })
      paintGuide(true)
    })
    return node
  }

  /**
   * ONE OF THE OTHER ERRANDS, PUT AWAY BUT NOT GONE.
   *
   * ==========================================================================
   *  A CIRCLE ON THE GRASS IS NOT A CARD (fixed vr2 — MAJOR)
   * ==========================================================================
   * These used to be bare 88 px CIRCLES with a monster face in them, floating
   * loose on the world at the right edge. Critic, major, off
   * `shots/critic-vr2-scenes/02-three-errands-stacked.png` and
   * `03-neighbour-request.png`: *"bare monster faces in circles plus a black
   * down-arrow, with no step strip and nothing that says 'this is a job you
   * took on'; next to the rounded-square language of every other control they
   * read as stray HUD buttons or debris."* And they do — the town is full of
   * round signpost badges with a picture in them, which is exactly what a
   * round badge with a picture in it looks like.
   *
   * Two changes, both about saying *what this is*:
   *   - the chip is a ROUNDED SQUARE on cream, the same shape language as the
   *     card's own face window (`.mt-errand-face`) and every button in the HUD;
   *   - it wears the ERRAND'S OWN PICTURE as a corner badge — the zoo, the
   *     flower, the path — which is the same picture that arrived on the
   *     `showArrival` card when the errand was handed over. Who asked, and
   *     what they asked for, in one 88 px target.
   * The third change is the tray they sit in — see `repaint()`.
   */
  const buildChip = (card: ErrandCard, onPick: () => void): HTMLElement => {
    const chip = el('div', 'mt-errand-chip')
    chip.setAttribute('role', 'button')
    chip.setAttribute('aria-label', card.def.devName)
    chip.dataset['id'] = card.id
    chip.append(monsterPortrait(giverLook(card.giver), 58))
    const what = el('span', 'mt-errand-chip-what')
    what.append(errandIcon(card.def.icon, 20))
    chip.append(what)
    onTap(chip, onPick)
    return chip
  }

  /**
   * ==========================================================================
   *  A FULL-SCREEN SHEET IS THE ONE THING ON THE GLASS (fixed vr1, round 1)
   * ==========================================================================
   * `#hud[data-panel='1']` handles the HUD's own panels (the creator, the shop,
   * photo mode) in CSS, and it is not enough: the zoo's feeding sheet, the
   * collection book and the kitchen are each a crew's OWN layer inside the HUD,
   * and none of them flips that flag — so the errand card was drawn over the top
   * of the zoo's own feeding sheet. Read off
   * `shots/fix-errands-vr1/08-zoo-feeding-sheet.png`.
   *
   * Reached BY SELECTOR, every rung optional, exactly the way `findDrawerTarget`
   * reaches the piece drawer and `tutorial.ts` reaches its tiles — crews never
   * import each other. A layer that is present but EMPTY is a closed sheet
   * (all three mount their layer once at install and fill it on open), so
   * `childElementCount` is the open/closed test, and if a crew renames a class
   * tomorrow this goes quiet and the deck simply stays visible: nothing breaks
   * and nothing lies.
   *
   * COST: three class lookups, eight times a second, and ONLY while the kid is
   * actually holding an errand — the deck is empty for most of the game's life
   * and the check is skipped entirely then.
   */
  const OVERLAYS = ['.mt-zoo-layer', '.mt-book-layer', '.mt-cook-layer']
  const overlayUp = (): boolean =>
    OVERLAYS.some((s) => (document.querySelector(s)?.childElementCount ?? 0) > 0)

  const syncCovered = (): boolean => {
    const covered = deck.childElementCount > 0 && overlayUp()
    if ((deck.dataset['covered'] === '1') !== covered) {
      deck.dataset['covered'] = covered ? '1' : '0'
    }
    return covered
  }

  /**
   * ONE BIG CARD, AND A FACE FOR EVERYBODY ELSE.
   *
   * Three full cards stacked do not fit between the camera cluster and the
   * hammer on the hand-me-down iPad (768 px tall) without shrinking the chips
   * back to unreadable — measured, not guessed. So the errand the kid is
   * actually following gets a proper card, and the others are 88 px faces
   * underneath it. Tapping a face swaps it into the card and the guide arrow
   * follows, which makes "several at once, in any order" a thing a kid can
   * SEE and DO rather than a thing the engine merely supports.
   */
  const repaint = (): void => {
    clear(deck)
    chrome = null
    // Somebody else's town is READ-ONLY, and that includes their errands: a
    // visitor looks, they do not carry a to-do list around a friend's house.
    const cards = save.visiting ? [] : liveCards()
    deck.dataset['tucked'] = tucked ? '1' : '0'
    if (cards.length === 0) {
      guide.style.display = 'none'
      return
    }
    if (focused === null || !cards.some((c) => c.id === focused)) {
      focused = cards[0]?.id ?? null
    }
    const front = cards.find((c) => c.id === focused) ?? cards[0]

    if (!tucked && front !== undefined) deck.append(buildCard(front))

    /*
     * ONE BOTTOM ROW: the tuck button and every other errand's face, together.
     *
     * They used to be two separate rows, and in BUILD MODE that put the tuck
     * button underneath the piece drawer — read off
     * `shots/errands/06-an-errand-arrives.png`, where it is sitting on top of
     * the drawer's tool row. The drawer's real height arrives as `--build-h`
     * (the HUD crew measures it), the deck is bounded by it in CSS, and one row
     * instead of two is what makes the deck fit inside that budget on the
     * hand-me-down iPad.
     */
    const bar = el('div', 'mt-errand-bar')
    bar.append(
      button(
        {
          icon: 'arrowDown',
          label: tucked ? 'Show the errands' : 'Put the errands away',
          className: 'mt-errand-tuck',
          cue: 'paper',
        },
        () => setTucked(!tucked),
      ),
    )
    const others = tucked ? cards : cards.filter((c) => c.id !== front?.id)
    for (const c of others) {
      bar.append(
        buildChip(c, () => {
          if (tucked) setTucked(false)
          setFocus(c.id)
          repaint()
        }),
      )
    }
    /*
     * HOW MANY ACROSS — AND WHY IT IS COMPUTED HERE (fixed vr2 — MAJOR).
     *
     * The bar was `row-reverse` + `wrap-reverse` + `justify-content: flex-start`
     * with a 280 px cap. With four things in it that lays out ONE item on the
     * top row and three underneath, right-aligned — the *"ragged staircase"* the
     * critic measured at (1076,410), (884,506) and (980,506). A wrap that puts
     * the orphan FIRST cannot look deliberate.
     *
     * So the row count is chosen instead of fallen into, and it is chosen to
     * come out square: 4 things go 2x2, not 3+1. `--mt-bar-cols` is the only
     * thing errands.css needs to size the tray, and the last row centres, so
     * an odd one out sits under the middle of the row above it rather than
     * hanging off an edge.
     *
     * Vertical is the scarce dimension on the hand-me-down iPad (the tray has
     * to finish above the hammer — `fit()` measures it), which is why five and
     * six go three-across rather than growing a third row.
     */
    const items = 1 + others.length
    bar.style.setProperty('--mt-bar-cols', String(items <= 3 ? items : items === 4 ? 2 : 3))
    deck.append(bar)
    fit()
    paintGuide(false)
  }

  /**
   * DOES THE DECK ACTUALLY FIT? MEASURED, NOT ASSUMED.
   *
   * The right-hand column is shared: the camera cluster above, the hammer
   * below, and in build mode the piece drawer, whose height only exists at
   * runtime. On the hand-me-down iPad (1024x768) in build mode the drawer's top
   * edge is at y=432 and the deck starts at y=232 — 200 px for a card (160), a
   * gap (10) and the face bar (88). It does not fit, and the first build simply
   * drew over the drawer's collapse chevron. Measured with
   * `tools/probe-errand-fit.mjs`, not guessed.
   *
   * So when there is not enough room the card goes COMPACT: the giver, the
   * counting dots and the step you are on, in one row 88 px tall. That is the
   * part a kid building a neighbour's request actually needs; the done/next
   * chips are context, and they come back the moment there is room for them
   * (leaving build mode is enough). Nothing is ever removed from the errand,
   * only from the picture of it.
   *
   * One layout read per REPAINT — and repaints are event-driven, a handful a
   * minute, never per frame.
   */
  const fit = (): void => {
    if (deck.childElementCount === 0) return
    deck.dataset['compact'] = '0'
    // A DECK THAT IS NOT ON THE GLASS CANNOT BE MEASURED. While a panel is open
    // the whole deck is display:none, and every rect below comes back as zero —
    // which used to fold the card into its compact row behind the panel, and it
    // was still folded when the panel closed. Nothing to fit; try again later.
    if (deck.getBoundingClientRect().height === 0) return
    /*
     * THE DRAWER'S HANDLE IS NOT INSIDE THE DRAWER'S BOX. `.mt-build-collapse`
     * is a child of `#mt-build` positioned at `top: -85px`, so it sticks up
     * ABOVE the rect `getBoundingClientRect()` reports — which is why the first
     * fit passed and the deck still landed on top of the handle. Measured off
     * `shots/errands/02c-build-mode.png`, not off the code.
     */
    const drawer = document.getElementById('mt-build')
    const handle = document.querySelector('.mt-build-collapse')
    /*
     * A ZERO-SIZE BOX IS NOT A CEILING. `offsetParent === null` catches
     * `display: none`, and it does NOT catch a control that is on the page but
     * has not been given a size yet — which reports `top: 0`, i.e. the harshest
     * possible limit, from the very top of the screen. That is what folded the
     * card on a brand new town: the hammer's row measured 0x0 at 0,0 while the
     * monster creator was up, `limit` came out -8, and every card in the game
     * went compact. `measureChrome()` below has always had this guard; `fit()`
     * did not.
     */
    const topOf = (n: Element | null): number => {
      if (n === null || (n as HTMLElement).offsetParent === null) return Infinity
      const b = n.getBoundingClientRect()
      return b.width === 0 || b.height === 0 ? Infinity : b.top
    }
    const drawerTop = Math.min(topOf(drawer), topOf(handle))
    const hammerTop = topOf(hud.querySelector('.mt-botright'))
    const limit = Math.min(drawerTop, hammerTop) - 8
    if (!Number.isFinite(limit)) return
    if (deck.getBoundingClientRect().bottom > limit) deck.dataset['compact'] = '1'
  }

  const setTucked = (on: boolean): void => {
    tucked = on
    // The engine's own record of "the card is away", so it survives a reload.
    for (const c of liveCards()) errands.setCardHidden(c.id, on)
    repaint()
  }

  const setFocus = (id: string | null): void => {
    const changed = focused !== id
    focused = id
    // The card IS the focus, so a change of focus is a change of card.
    if (changed && !tucked) repaint()
    bus.emit('errand:pointAt', { id })
  }

  // -- the guide arrow ------------------------------------------------------

  let lastTarget: GuideTarget | null = null
  /** The last projection of the target, in canvas px. QA surface only. */
  let lastPx: { x: number; y: number; behind: boolean } | null = null
  /**
   * WHAT `lastTarget` WAS RESOLVED FOR. Without it the cache is a trap: the
   * talk step resolves to `here` (no arrow), and every later frame then reused
   * that answer — so the arrow stayed down for ever after the first step, on
   * exactly the step that most needs pointing at. Caught by `errand-check`.
   */
  let lastKey = ''

  /**
   * THE HUD IS AN EXCLUSION ZONE FOR THE ARROW, NOT SOMETHING IT HIDES BEHIND.
   *
   * The arrow lands wherever the world is, so sooner or later it is behind the
   * cards, the camera cluster, the coin purse or the piece drawer. The first
   * answer was to FADE THE DECK out of the way, the way the camera cluster gets
   * out of the build ghost's way — and on the hand-me-down iPad (1024x768) the
   * target sat under the deck in all four camera rotations, so the deck was
   * permanently at 30 % and unreadable. Measured, not guessed.
   *
   * So the ARROW moves instead: pushed to the nearest edge of whatever is in
   * the way and laid down pointing the direction, exactly as it already does
   * for a target off the edge of the glass. Nothing is ever hidden — not the
   * arrow, and not a single control.
   *
   * The boxes are CACHED in canvas space and only re-measured when the deck,
   * the mode or the viewport changes. Six `getBoundingClientRect()` calls on a
   * projection pass eight times a second is a synchronous layout the older iPad
   * cannot afford; the same lesson `hud.ts` writes up at length.
   */
  const CHROME = [
    '.mt-purse',
    '.mt-leftstack',
    '.mt-cam',
    '.mt-errands',
    '.mt-botright',
    '#mt-build',
    '.mt-build-collapse',
  ]
  const CLEAR_PX = 12
  /**
   * HALF THE ARROW, NOT THE ANCHOR POINT.
   *
   * The first version tested the point the arrow POINTS AT against the chrome,
   * and the arrow floats 46 px above that point — so an arrow aimed at open
   * ground just below the camera cluster was drawn squarely on top of it and
   * the test said everything was fine. Everything below works in the arrow's
   * own CENTRE and its box, which is the thing that has to stay visible.
   */
  const HALF = 40
  /**
   * ============ AND THE BOX IS NOT CENTRED ON THE POINT (fixed vr1) ============
   *
   * `left`/`top` on `.mt-guide` resolve through `translate(-50%, -100%)`, so the
   * point written out is the arrow's BOTTOM CENTRE, and the 72 px drawing hangs
   * ABOVE it. The test above modelled it as a 40 px square centred on that point
   * — 32 px too short upward and 40 px too generous downward — so an arrow
   * parked just under a control's top edge measured clear while its head was
   * drawn across it. Left 672 px² of arrow on the left rail in the zoo, from all
   * four rotations, after the edge-dock fix below had already run.
   *
   * These three numbers are the element's real footprint, and there is only one
   * source for them: `.mt-guide` is `width: 72px; height: 72px` in errands.css.
   * A few px of slack for the bob keyframe, which lifts it while it plays.
   */
  /**
   * ...AND THE ARROW HAS TWO ANCHOR CONVENTIONS, NOT ONE. This is the last of
   * them and it is the one that actually put the arrow on the turn-right button.
   *
   *   free    `.mt-guide`                 translate(-50%, -100%)  -> left/top is its FOOT
   *   docked  `.mt-guide[data-edge='1']`  translate(-50%,  -50%)  -> left/top is its CENTRE
   *
   * Two conventions, one collision box, and the wrong one applied to the state
   * the defect lives in: the docked arrow was modelled as hanging 78 px ABOVE
   * the point, while the browser was drawing it 36 px BELOW that — so the test
   * was clear by 72 px of pure fiction. Both spans are declared here, beside the
   * CSS rule each of them mirrors, and every test takes the one that matches the
   * state it is about to write.
   */
  const SPAN_FREE = { up: 84, down: 6 } // 72 tall above the foot, + 12 of bob
  const SPAN_DOCKED = { up: 52, down: 52 } // 72 square about the centre, rotated
  type Span = { up: number; down: number }
  /**
   * How far above the point it is aimed at the arrow's FOOT sits. The arrow
   * hangs above its target and this is the whole of the gap. One number, used
   * for the collision test and for the CSS, because two numbers is exactly what
   * went wrong the first time.
   */
  const GUIDE_FOOT = 10
  let chrome: Array<{ l: number; t: number; r: number; b: number }> | null = null

  /**
   * ============ AN EMPTY MEASUREMENT IS NEVER CACHED (fixed vr1) ============
   *
   * THE ACTUAL CAUSE of the vr1 capture, and the other two faults in this
   * neighbourhood only made it worse. Walking through a door hides the whole HUD
   * for the length of the scene transition — every one of these selectors
   * measures 0x0 or has no `offsetParent` — and the arrow keeps projecting
   * throughout. The first projection inside the new scene therefore cached
   * `chrome = []`, and the cache is only invalidated by a resize, an
   * orientation change or a panel. So for the rest of that visit to the zoo the
   * arrow believed there were NO CONTROLS ON THE SCREEN and docked wherever it
   * liked — which is how a guide arrow ended up sitting on the turn-right
   * button, and why it stayed there through every camera rotation.
   *
   * Measured: `.mt-leftstack` overlapped by 2494 px² at all four rotations in
   * venue #3, with all three of the other fixes already in.
   *
   * "There is nothing on screen" is a real answer for about half a second and a
   * wrong answer for ever after, so it is not remembered — the same rule
   * `src/routine/places.ts` writes up for an empty landmark reply. Re-measuring
   * costs six `getBoundingClientRect` calls and only happens while the HUD is
   * genuinely absent, which is exactly when there is nothing else to do.
   */
  const measureChrome = (): void => {
    if (chrome !== null) return
    const c = ctx.canvas.getBoundingClientRect()
    const out: Array<{ l: number; t: number; r: number; b: number }> = []
    for (const sel of CHROME) {
      const n = hud.querySelector(sel) ?? document.querySelector(sel)
      if (n === null || (n as HTMLElement).offsetParent === null) continue
      const b = n.getBoundingClientRect()
      if (b.width === 0 || b.height === 0) continue
      out.push({ l: b.left - c.left, t: b.top - c.top, r: b.right - c.left, b: b.bottom - c.top })
    }
    if (out.length === 0) return
    chrome = out
  }

  /** The first control the arrow DRAWN at this anchor would land on top of. */
  const blockedBy = (
    cx: number,
    cy: number,
    span: Span,
  ): { l: number; t: number; r: number; b: number } | null => {
    measureChrome()
    for (const box of chrome ?? []) {
      if (cx - HALF < box.r && box.l < cx + HALF && cy - span.up < box.b && box.t < cy + span.down) {
        return box
      }
    }
    return null
  }

  /** Where the arrow's anchor may sit and still draw the whole arrow on the glass. */
  const dockX = (x: number, w: number): number => Math.min(w - EDGE_PAD_PX, Math.max(EDGE_PAD_PX, x))
  const dockY = (y: number, h: number, span: Span): number =>
    Math.min(h - span.down - 4, Math.max(span.up + 4, y))

  /** Out of the box by its nearest edge. Predictable, and always still on the glass. */
  const pushOut = (
    x: number,
    y: number,
    box: { l: number; t: number; r: number; b: number },
    w: number,
    h: number,
    span: Span,
  ): { x: number; y: number } => {
    const options = [
      { x: box.l - HALF - CLEAR_PX, y, d: x - box.l },
      { x: box.r + HALF + CLEAR_PX, y, d: box.r - x },
      // UP moves the arrow's BOTTOM above the control; DOWN moves its TOP below
      // it. Using one half-extent for both is what left the head on the rail.
      { x, y: box.t - span.down - CLEAR_PX, d: y - box.t },
      { x, y: box.b + span.up + CLEAR_PX, d: box.b - y },
    ].sort((a, b) => a.d - b.d)
    for (const o of options) {
      if (o.x < EDGE_PAD_PX || o.x > w - EDGE_PAD_PX) continue
      if (o.y < span.up + 4 || o.y > h - span.down - 4) continue
      if (blockedBy(o.x, o.y, span) !== null) continue
      return { x: o.x, y: o.y }
    }
    return { x: dockX(x, w), y: dockY(y, h, span) }
  }

  /**
   * ==========================================================================
   *  A DOCKED ARROW LIVES ON THE RIM, NOT AGAINST THE CARD (fixed vr2)
   * ==========================================================================
   * When the thing an errand wants is off the edge of the glass, the arrow lies
   * down and points the way. It used to get there by clamping to the border and
   * then being shoved out of whatever control it landed on by `pushOut`, which
   * picks the NEAREST free edge of that control — and the control it lands on
   * is very often the errand deck itself, whose nearest free edge is its left
   * side. Measured from thirteen town spots with the `make` step live
   * (`tools/critic-kid-day-r2-make.mjs`): eleven docked, repeatedly at
   * x = 768 or 756 — the boundary of the right-hand chrome column — with the
   * card immediately to the right of the arrow's own point.
   * `shots/kid-day-r2/walk-00-start.png` is an arrow leaning on a picture card
   * and appearing to point INTO it. A pre-reader has one instruction and it has
   * to be about the world.
   *
   * So an off-glass target docks on the GLASS EDGE it lies beyond, and when
   * something is already sitting there the arrow slides ALONG that edge instead
   * of stepping inward. The rim on the side the thing is on is a place a kid can
   * read as "that way"; a spot two thirds of the way in, resting on a card, is
   * not.
   *
   * The scan is coarse (24 px) and bounded, and every test is against the same
   * CACHED chrome boxes `blockedBy` already uses — no layout reads, so this
   * costs the same handful of rectangle compares the old push did.
   */
  const SLIDE_STEP_PX = 24
  /**
   * Walk one rim outward from the spot the arrow wants, nearest first, and stop
   * at the first place the whole arrow fits. `alongY` picks the rim: true is a
   * left/right edge (x pinned, y varies), false is top/bottom.
   *
   * With nothing measured yet — the HUD is off screen for the length of a scene
   * transition — the very first candidate answers, so this cannot turn into a
   * hundred layout reads a frame. Once the boxes ARE cached it is pure
   * arithmetic over at most seven rectangles.
   */
  const edgeSlide = (
    fixed: number,
    start: number,
    lo: number,
    hi: number,
    alongY: boolean,
    span: Span,
  ): { x: number; y: number } | null => {
    const at = Math.min(hi, Math.max(lo, start))
    for (let d = 0; d <= hi - lo; d += SLIDE_STEP_PX) {
      for (const v of d === 0 ? [at] : [at - d, at + d]) {
        if (v < lo || v > hi) continue
        const x = alongY ? fixed : v
        const y = alongY ? v : fixed
        if (blockedBy(x, y, span) === null) return { x, y }
      }
    }
    return null
  }

  const dockOnRim = (
    cx: number,
    cy: number,
    dx: number,
    dy: number,
    w: number,
    h: number,
    span: Span,
  ): { x: number; y: number } | null => {
    const loY = span.up + 4
    const hiY = h - span.down - 4
    const loX = EDGE_PAD_PX
    const hiX = w - EDGE_PAD_PX
    // The side the thing is on comes first; the other axis is the fallback for
    // a rim that is full end to end (four controls in one column).
    const sides: Array<() => { x: number; y: number } | null> = [
      () => edgeSlide(dx >= 0 ? hiX : loX, cy, loY, hiY, true, span),
      () => edgeSlide(dy >= 0 ? hiY : loY, cx, loX, hiX, false, span),
    ]
    if (Math.abs(dy) > Math.abs(dx)) sides.reverse()
    for (const side of sides) {
      const hit = side()
      if (hit !== null) return hit
    }
    return null
  }

  /**
   * ==========================================================================
   *  THE ARROW GOES INTO THE DRAWER (fixed vr1 — MAJOR)
   * ==========================================================================
   * A neighbour request walks a kid across town and then stops at the hammer.
   * Critic: holding *"my garden needs three flowers"*, tapping the hammer opens
   * the drawer on the **blocks** tab — a wooden block and a stone block. The
   * flower is `plant_flower`, category `plant`, and it lives behind the
   * **garden** tab. Nothing anywhere said so. *"A 5-year-old might match the
   * card's plant icon to the garden tab's icon; a 3-year-old has to try all
   * seven."*
   *
   * A kid already has exactly one instruction in this game — go where the arrow
   * is — so the arrow finishes the job instead of a new hint being invented:
   *
   *   the piece is not on screen  -> point at the TAB that holds it
   *   the piece is on screen      -> point at the TILE
   *   the piece is armed          -> stop, and point at the GROUND (below)
   *
   * That is the whole chain from card to placed flower, and every rung of it is
   * the same picture the kid has been following since the errand arrived.
   *
   * WHY IT IS A DOM LOOKUP AND NOT AN IMPORT. Crews never import each other, so
   * this reaches for the drawer exactly the way `src/ui/tutorial.ts` already
   * does for its paw (`#mt-build .mt-tile[data-free="true"]`) — by selector,
   * with every rung optional. If the build crew renames a class tomorrow this
   * goes quiet and the world arrow takes over; nothing breaks and nothing lies.
   */
  const TAB_OF: Readonly<Record<string, readonly string[]>> = {
    // Mirrors `TAB_DEFS` in src/build/palette.ts, INCLUDING its fold-away
    // fallbacks: a thin tab folds into another one and stops existing, so each
    // category names its tab and then where that tab's pieces go instead.
    wall: ['walls'],
    door: ['walls'],
    window: ['walls'],
    roof: ['roofs', 'walls'],
    floor: ['floors', 'paths'],
    path: ['paths'],
    stair: ['paths'],
    block: ['blocks', 'walls'],
    fence: ['fences', 'paths'],
    plant: ['garden'],
    tree: ['garden'],
    water: ['garden'],
    furniture: ['furniture', 'decor'],
    bed: ['furniture', 'decor'],
    kitchen: ['kitchen', 'furniture', 'decor'],
    bath: ['kitchen', 'furniture', 'decor'],
    appliance: ['kitchen', 'furniture', 'decor'],
    decor: ['decor'],
    light: ['decor'],
    toy: ['decor'],
    townProp: ['decor'],
    venueFixture: ['shop'],
  }

  /** Cached between projections — see the cost note on `PROJECT_EVERY`. */
  let drawerHit: HTMLElement | null = null
  let drawerAt = -1

  const findDrawerTarget = (card: ErrandCard): HTMLElement | null => {
    if (app.mode !== 'build') return null
    const step = card.step
    if (step === null || step.kind !== 'build') return null
    const drawer = document.getElementById('mt-build')
    if (drawer === null || drawer.offsetParent === null) return null
    const answers = answersFor(step)
    if (answers.length === 0) return null
    const names = new Set(answers.map((a) => a.devName))
    const tiles = [...drawer.querySelectorAll<HTMLElement>('.mt-tile')]
    const labelOf = (n: Element): string => n.getAttribute('aria-label') ?? ''
    // ALREADY HOLDING ONE? Then the drawer has done its job and the arrow's
    // place is out in the world, on the spot the piece is wanted.
    const armed = tiles.find((t) => t.dataset['selected'] === 'true')
    if (armed !== undefined && names.has(labelOf(armed))) return null
    const tile = tiles.find((t) => names.has(labelOf(t)) && t.getBoundingClientRect().width > 0)
    if (tile !== undefined) return tile
    // Behind another tab. Point at the tab, in fold order.
    for (const a of answers) {
      for (const tabId of TAB_OF[a.category] ?? []) {
        const tab = drawer.querySelector<HTMLElement>(`.mt-build-tabs [aria-label="${tabId}"]`)
        if (tab !== null && tab.getBoundingClientRect().width > 0) return tab
      }
    }
    return null
  }

  /**
   * The arrow, sitting on a CONTROL rather than on the world.
   *
   * THE TIP LANDS INSIDE THE THING (fixed vr1, round 1). At `+8` the arrow's
   * point stopped a clear 45 px short of the tile it meant and its body was
   * drawn across the TAB ROW ABOVE — so the picture said "fences" while the
   * measurement said "Flower patch". Read off `04-drawer-arrow-on-the-tile.png`.
   * The drawer's rows are only ~56 px apart on the hand-me-down iPad, so the tip
   * has to overlap its own target to be unambiguous about which row it means.
   */
  const DOM_BITE_PX = 26
  const pointAtElement = (node: HTMLElement): void => {
    const c = ctx.canvas.getBoundingClientRect()
    const r = node.getBoundingClientRect()
    guide.style.display = 'flex'
    guide.dataset['edge'] = '0'
    guide.dataset['dom'] = '1'
    guide.style.left = `${Math.round(r.left + r.width / 2 - c.left)}px`
    guide.style.top = `${Math.round(r.top - c.top + Math.min(DOM_BITE_PX, r.height / 2))}px`
    guide.style.removeProperty('--mt-guide-rot')
  }

  const paintGuide = (force: boolean): void => {
    if (save.visiting || tucked || syncCovered()) {
      guide.style.display = 'none'
      return
    }
    // ONE card, not all of them. `errands.cards()` rebuilds a full card object
    // per running errand (including a fresh `strip` array) and this runs eight
    // times a second — the deck's whole per-frame budget went on garbage for
    // errands nobody is following.
    const card = focused === null ? null : errands.card(focused)
    if (card === null || card.done) {
      guide.style.display = 'none'
      delete guide.dataset['dom']
      lastTarget = null
      lastKey = ''
      return
    }

    // The drawer wins while it is the thing standing between a kid and the
    // errand. Re-measured every other projection (four times a second) and only
    // ever in build mode holding a build step — see the note above.
    if (app.mode === 'build') {
      // `tick >> 3` = four times a second at 30 Hz, half the arrow's own rate.
      const slot = tick >> 3
      if (force || slot !== drawerAt) {
        drawerHit = findDrawerTarget(card)
        drawerAt = slot
      }
      if (drawerHit !== null && drawerHit.isConnected) {
        pointAtElement(drawerHit)
        return
      }
    } else drawerHit = null
    delete guide.dataset['dom']

    if (card.pointAt === null) {
      guide.style.display = 'none'
      lastTarget = null
      lastKey = ''
      return
    }
    const from: Vec3 = app.playerPos
    const key = `${card.id}|${card.stepIndex}|${card.pointAt}`
    /*
     * A TARGET THAT DOES NOT RESOLVE IS AN ANSWER, AND IT IS CACHED LIKE ONE.
     *
     * `stale` used to include `lastTarget === null`, so an errand pointing at a
     * place no crew has shipped yet (`pointAt: 'pond'`) re-ran the whole lookup
     * on every projection, for ever. Measured at 5.2 ms/frame on `ipad-old`
     * with three errands up — a third of the entire frame budget, spent
     * deciding the same "no" over and over.
     */
    if (!force && key === lastKey && lastTarget === null) {
      guide.style.display = 'none'
      return
    }
    const stale = force || key !== lastKey
    const target = stale ? resolveTarget(card.pointAt, from) : lastTarget
    lastTarget = target
    lastKey = key
    if (target === null || target.at === null) {
      guide.style.display = 'none'
      return
    }
    const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, target.at)
    /**
     * WHERE THE THING ACTUALLY IS, in the same canvas space the arrow reports.
     * Read-only, QA-only, and the reason it exists: "the arrow is parked
     * against the card" and "the arrow is beside the card because the thing IS
     * behind the card" look identical from the arrow's position alone. See
     * `tools/fix-errands-vr2.mjs`.
     */
    lastPx = { x: Math.round(p.x), y: Math.round(p.y), behind: p.behind }
    if (p.behind) {
      guide.style.display = 'none'
      return
    }
    if (canvasBox === null) {
      const r = ctx.canvas.getBoundingClientRect()
      canvasBox = { w: r.width, h: r.height }
    }
    const { w, h } = canvasBox
    /*
     * ============ TEST THE POINT YOU ARE ABOUT TO WRITE (fixed vr1) ============
     *
     * The chrome test ran at `p.y - LIFT` and the CSS was then written at
     * `p.y - 10` — thirty-six pixels apart. So the band that was tested sat well
     * above the band that was drawn, and an arrow whose head was clear of the
     * left rail while its foot stood on it passed. That is the last of the three
     * faults behind the vr1 capture; the others are written up at `SPAN_FREE`,
     * at `measureChrome` and at the edge-dock below.
     *
     * There is now ONE anchor per branch, and it is the one the browser lays out
     * from. `LIFT` is gone as a second opinion about where the arrow is.
     */
    const cx = p.x
    const cy = p.y - GUIDE_FOOT
    const onGlass =
      cx >= EDGE_PAD_PX &&
      cx <= w - EDGE_PAD_PX &&
      cy >= SPAN_FREE.up + 4 &&
      cy <= h - SPAN_FREE.down - 4
    guide.style.display = 'flex'

    const box = onGlass ? blockedBy(cx, cy, SPAN_FREE) : null
    if (onGlass && box === null) {
      guide.dataset['edge'] = '0'
      guide.style.left = `${Math.round(cx)}px`
      guide.style.top = `${Math.round(cy)}px`
      guide.style.removeProperty('--mt-guide-rot')
      return
    }

    /*
     * Either off the edge of the glass, or behind a control. Either way the
     * arrow lies down at the boundary and points the way instead of hovering
     * over something nobody can see.
     *
     * ============ AND THE EDGE IS WHERE THE CONTROLS ARE (fixed vr1) ============
     *
     * The chrome test above only ran `onGlass`, and the off-glass branch clamped
     * straight to the border with no test at all — so an arrow aimed at
     * something off screen docked into whichever corner it was pointing toward,
     * and every corner of this game has a control in it. Photographed in venue
     * #3: a big solid orange arrow sitting squarely on the TURN-RIGHT button,
     * with the button's own outline showing round the edges of it. The critic
     * read that as a pressed state that changes a paired control's shape and
     * size, which is a fair reading and a worse bug than the one it is —
     * whatever it is read as, it is a hint drawn on top of a control, and this
     * file's own rule two comments up says that may not happen.
     *
     * So the SAME avoidance runs on both branches, and it runs to a fixed point:
     * push out, re-clamp to the glass, and test again, at most three times (four
     * chrome boxes can meet in a corner; three passes has always been enough and
     * the bound is what makes this safe to call eight times a second).
     */
    // FROM HERE ON THE ARROW IS DOCKED, so every test uses the docked span —
    // the CSS below swaps the transform to a CENTRE anchor and the box moves
    // 36 px with it. See `SPAN_DOCKED`.
    const dx = p.x - w / 2
    const dy = p.y - h / 2
    /*
     * TWO DIFFERENT QUESTIONS, TWO ANSWERS (fixed vr2 — see `dockOnRim`).
     *
     *   off the glass    the thing is somewhere over there. The arrow belongs on
     *                    the RIM on that side, sliding along it to get clear —
     *                    never stepping inward to lean on the errand card.
     *   behind a control the thing is right there, under something. The arrow
     *                    belongs as close to it as it can get, which is the
     *                    nearest free edge of whatever is covering it.
     */
    let out =
      box === null
        ? (dockOnRim(cx, cy, dx, dy, w, h, SPAN_DOCKED) ?? {
            x: dockX(cx, w),
            y: dockY(cy, h, SPAN_DOCKED),
          })
        : pushOut(cx, cy, box, w, h, SPAN_DOCKED)
    for (let pass = 0; pass < 3; pass++) {
      const hit = blockedBy(out.x, out.y, SPAN_DOCKED)
      if (hit === null) break
      const moved = pushOut(out.x, out.y, hit, w, h, SPAN_DOCKED)
      out = { x: dockX(moved.x, w), y: dockY(moved.y, h, SPAN_DOCKED) }
    }
    /*
     * AND IF THE PUSH GAVE UP, THE RIM IS STILL THERE (fixed vr2).
     *
     * `pushOut` returns a plain CLAMP when none of a box's four edges is free,
     * and the loop above then re-clamps to the same spot every pass — so an
     * arrow wedged between the camera cluster and the errand deck stayed drawn
     * across both of them. Measured from the plaza with the `make` step live:
     * 826,206, whose 72 px body covers the bottom of `.mt-cam` and the top of
     * `.mt-errands`. The rim walk is a strictly better last resort than a clamp,
     * because it only ever returns somewhere the whole arrow fits — and if even
     * it finds nothing, we are no worse off than before.
     */
    if (blockedBy(out.x, out.y, SPAN_DOCKED) !== null) {
      out = dockOnRim(out.x, out.y, dx, dy, w, h, SPAN_DOCKED) ?? out
    }
    guide.dataset['edge'] = '1'
    guide.style.left = `${Math.round(out.x)}px`
    guide.style.top = `${Math.round(out.y)}px`
    // The drawing points UP, so 0deg is -Y and the rotation is atan2 + 90.
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90
    guide.style.setProperty('--mt-guide-rot', `${Math.round(deg)}deg`)
  }

  // -- the arrival ----------------------------------------------------------

  /**
   * A monster waved and gave you something to do. A picture, big, in the middle
   * of the screen, that leaves on its own. Nothing to tap, nothing to dismiss,
   * nothing blocked underneath it (`pointer-events: none`).
   */
  /**
   * QA ONLY, and off unless a tool asks — the same shape of opt-in the monster
   * creator's `allowFirstRunUnderAutomation` uses, for the same reason.
   *
   * A full-page screenshot of this game costs FOUR SECONDS (measured with
   * `tools/probe-arrival.mjs`), so a card that is deliberately on screen for
   * 4.2 s can never be photographed while it is leaving — and an unphotographed
   * picture is one nobody has ever actually looked at. This holds it open for
   * the camera. Nothing a kid can tap reaches it, and the "it lets itself out"
   * assertion runs with the hold OFF.
   */
  let holdArrival = false

  const showArrival = (giver: string, icon: string): void => {
    arrival?.remove()
    const node = el('div', 'mt-arrival')
    node.append(face(giver, 88))
    const what = errandIcon(icon, 76)
    what.classList.add('mt-arrival-what')
    node.append(what)
    if (holdArrival) node.dataset['hold'] = '1'
    hud.append(node)
    arrival = node
    if (holdArrival) return
    window.setTimeout(() => {
      node.remove()
      if (arrival === node) arrival = null
    }, ARRIVAL_MS)
  }

  // -- wiring ---------------------------------------------------------------
  //
  // These five listeners are the ones `tools/wiring-check.mjs` was holding open
  // for this crew under "CREW: hud". Their KNOWN_SINKS lines are deleted.

  bus.on('errand:offered', (e) => {
    // FROM THE DEFINITION, NOT FROM THE RUNNING CARD. `errand:offered` is
    // emitted BEFORE the errand is granted, so there is no card yet — and the
    // first version quietly fell through to a generic envelope for every
    // arrival, which is a picture that says "a thing happened" instead of
    // "the animals are hungry". Read off the close-up capture.
    const def = getErrand(e.id)
    showArrival(e.giver, def?.icon ?? 'note')
    bus.emit('audio:cue', { cue: 'chime_happy' })
  })
  bus.on('errand:granted', (e) => {
    tucked = false
    focused = e.id
    repaint()
  })
  bus.on('errand:progressed', () => repaint())
  bus.on('errand:stepDone', () => {
    bus.emit('audio:cue', { cue: 'sparkle_soft' })
    repaint()
  })
  bus.on('errand:finished', () => repaint())
  bus.on('errand:cardHidden', (e) => {
    // Somebody other than the tuck button put a card away (the harness, a
    // future crew). Keep the deck honest about it.
    if (e.hidden !== tucked) repaint()
  })
  bus.on('errand:pointAt', () => paintGuide(true))

  bus.on('save:loaded', () => {
    // `hidden` is saved per run, so a deck tucked yesterday is tucked today.
    tucked = liveCards().length > 0 && liveCards().every((c) => c.hidden)
    focused = null
    repaint()
  })
  bus.on('scene:entered', () => {
    lastTarget = null
    lastKey = ''
    repaint()
  })
  /**
   * A VISITOR CARRIES NO TO-DO LIST. `repaint()` already refuses to draw a deck
   * while `save.visiting`, but nothing was telling it the visit had started —
   * so the cards sat there over somebody else's town. The visit crew's own
   * events are the trigger (not the save layer's, which is theirs to own).
   */
  bus.on('visit:entered', () => repaint())
  bus.on('visit:left', () => repaint())
  const relayout = (): void => {
    chrome = null
    fit()
    paintGuide(true)
  }
  bus.on('app:modeChanged', relayout)
  bus.on('build:paletteToggled', relayout)
  bus.on('build:paletteMounted', relayout)
  /*
   * A PANEL CLOSING IS A LAYOUT CHANGE. `ui:panel` used only to drop the cached
   * chrome boxes; nothing re-fitted the card, so a deck that was hidden while
   * the panel was up came back at whatever size it was last measured at. It runs
   * on the NEXT frame because the HUD's own `data-panel` flip and this listener
   * are on the same event, and the boxes are not real until the style has landed.
   */
  bus.on('ui:panel', () => {
    window.requestAnimationFrame(relayout)
  })
  window.addEventListener('resize', relayout)
  bus.on('ui:cameraRotate', () => paintGuide(true))
  bus.on('ui:cameraZoom', () => paintGuide(true))

  engine.addSystem({
    id: 'errands.guide',
    order: 93,
    fixed: () => {
      tick++
      if (tick % PROJECT_EVERY !== 0) return
      // A sheet can go up or come down without a repaint (another crew's layer
      // opens on its own event), so the "am I covered?" answer is refreshed on
      // the arrow's own cadence. Free when the deck is empty — see `syncCovered`.
      const covered = syncCovered()
      if (covered) {
        guide.style.display = 'none'
        return
      }
      if (guide.style.display === 'none' && focused === null) return
      paintGuide(false)
    },
  })

  repaint()

  // -- test surface (its own global; window.__harness stays architect-owned) --
  window.__errands = {
    ready: true,
    cards: () =>
      errands.cards().map((c) => ({
        id: c.id,
        giver: c.giver,
        stepIndex: c.stepIndex,
        kind: c.step?.kind ?? null,
        have: c.have,
        need: c.need,
        hidden: c.hidden,
        done: c.done,
        strip: c.strip.map((s) => ({ icon: s.icon, done: s.done, current: s.current })),
        pointAt: c.pointAt,
      })),
    boxes: () =>
      [...deck.querySelectorAll<HTMLElement>('.mt-errand, .mt-errand-chip, .mt-btn')].map((n) => {
        const r = n.getBoundingClientRect()
        return {
          label: n.dataset['id'] ?? n.getAttribute('aria-label') ?? n.className,
          w: Math.round(r.width),
          h: Math.round(r.height),
        }
      }),
    text: () => deck.textContent ?? '',
    /**
     * The face this giver id actually gets, after any override the NPC crew has
     * published (`errand:giverLook`). Additive, read-only, and here so a gate
     * can prove the card wears the SAME monster the kid is looking at rather
     * than the hash-derived stand-in — which is a thing only an integration
     * test spanning both crews can see.
     */
    giverLook: (giver: string) => giverLook(giver),
    focus: () => focused,
    setFocus: (id) => setFocus(id),
    tuck: (on) => setTucked(on),
    tucked: () => tucked,
    guide: () => {
      if (guide.style.display === 'none') return null
      const r = guide.getBoundingClientRect()
      const canvas = ctx.canvas.getBoundingClientRect()
      return {
        x: Math.round(r.left + r.width / 2 - canvas.left),
        y: Math.round(r.top + r.height / 2 - canvas.top),
        edge: guide.dataset['edge'] === '1',
        target: lastTarget === null ? null : lastTarget.kind,
        pointAt: errands.cards().find((c) => c.id === focused)?.pointAt ?? null,
        /**
         * WHAT THE ARROW THINKS IS IN ITS WAY. Added for the vr1 "orange arrow
         * on the turn-right button" defect: the arrow's own position is not
         * enough to debug it, because the same wrong answer comes out of an
         * empty chrome list, a stale one and a bad push. `null` means the list
         * has never been measured; `[]` means it measured nothing, which is the
         * state that produced the capture.
         */
        chrome: chrome === null ? null : chrome.map((b) => [b.l, b.t, b.r, b.b].map(Math.round)),
        /** Where the thing itself projected to, whether or not it is on glass. */
        at: lastPx,
        /** Non-null when the arrow is on a CONTROL: the drawer tab or tile. */
        onControl:
          guide.dataset['dom'] === '1' && drawerHit !== null
            ? (drawerHit.getAttribute('aria-label') ?? drawerHit.className)
            : null,
      }
    },
    arrivalUp: () => arrival !== null,
    /** QA ONLY: keep the arrival card on screen long enough to photograph it. */
    holdArrival: (on) => {
      holdArrival = on
      if (!on && arrival !== null) {
        const node = arrival
        delete node.dataset['hold']
        window.setTimeout(() => {
          node.remove()
          if (arrival === node) arrival = null
        }, ARRIVAL_MS)
      }
    },
    repaint,

    // -- the crew's non-UI halves, on the same surface so a tool needs one ---
    auto: (on) => setAutoOffers(on),
    offers: () => offerReport(),
    offerNow: (id) => offerNow(id),
    requests: () => requestReport(),
    /** How many matching pieces are REALLY standing in the town. */
    townCount: (category, sceneId) =>
      townCount({ kind: 'build', category, count: 1, icon: 'block', ...(sceneId === undefined ? {} : { sceneId }) }),
    places: () => knownPlaceIds(),
  }

  return { repaint, focus: () => focused, setFocus }
}

export interface ErrandsTestApi {
  readonly ready: boolean
  cards(): Array<{
    id: string
    giver: string
    stepIndex: number
    kind: string | null
    have: number
    need: number
    hidden: boolean
    done: boolean
    strip: Array<{ icon: string; done: boolean; current: boolean }>
    pointAt: string | null
  }>
  /** Every tappable thing in the deck, measured. Nothing may be under 88 px. */
  boxes(): Array<{ label: string; w: number; h: number }>
  /** MUST BE EMPTY — LAW #4. The deck is pictures, all the way down. */
  text(): string
  /** The face a giver id resolves to right now, override included. */
  giverLook(giver: string): MonsterConfig
  focus(): string | null
  setFocus(id: string | null): void
  tuck(on: boolean): void
  tucked(): boolean
  guide(): {
    x: number
    y: number
    edge: boolean
    target: string | null
    pointAt: string | null
    /** The drawer control the arrow is standing on, if it is on one. */
    onControl: string | null
    /** Every HUD box the arrow must keep off, in canvas px: [l,t,r,b]. */
    chrome: number[][] | null
    /** Where the target itself projected to, in canvas px. */
    at: { x: number; y: number; behind: boolean } | null
  } | null
  arrivalUp(): boolean
  /** QA ONLY: hold the arrival card open for a capture. No kid-reachable path. */
  holdArrival(on: boolean): void
  repaint(): void

  /** Arrivals are OFF under an automated browser until a tool asks. */
  auto(on: boolean): void
  offers(): {
    blocked: boolean
    auto: boolean
    quietFor: number
    running: number
    offerable: string[]
    doable: string[]
  }
  offerNow(id: string): boolean
  /** The town-state ratchet, exposed: what is really there vs what the card says. */
  requests(): Array<{
    id: string
    step: number
    kind: string
    townHave: number
    baseline: number
    cardHave: number
    need: number
  }>
  townCount(category: string, sceneId?: string): number
  /** Every place key the guide arrow could resolve in this scene. */
  places(): string[]
}

declare global {
  interface Window {
    __errands?: ErrandsTestApi
  }
}
